/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

import type { NewAccountData } from '../../components/Accounts/types';

export type PartnerAccountMatrixRequest = NewAccountData & {
  newAccountId: string;
};

export type PartnerAccountMatrixResponse = {
  success: boolean;
  errorMessage: string;
};

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  PartnerAccountMatrixResponse | undefined,
  (requestParams: PartnerAccountMatrixRequest) => void,
  () => void,
];

function usePostPartnerAccountMatrix({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerAccountMatrixRequest>();
  const [response, setResponse] = useState<PartnerAccountMatrixResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerAccountMatrixRequest) => {
      setDataFetchingInfo(true, 'sending account matrix data', 'fullScreen');

      const { Success, ErrorMessage } = await api.postPartnerAccountMatrix(buildParams(requestParams), source);

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: PartnerAccountMatrixRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: PartnerAccountMatrixRequest): vp.PartnerAccountMatrixRequest {
    const {
      newAccountId,
      accountIntendedTransactionType,
      NTPFundingEFTEnabled,
      NTPFundingEFTAmount,
      NTPFundingACHEnabled,
      NTPFundingACHAmount,
      NTPFundingACHSameDayEnabled,
      NTPFundingACHSameDayAmount,
      NTPFundingInteracEnabled,
      NTPFundingInteracAmount,
      NTPFundingCreditCardEnabled,
      NTPFundingCreditCardAmount,
      NTPFundingFedwireEnabled,
      NTPFundingFedwireAmount,
      NTPFundingTotalAmount,
      NTPFundingAvgFundedAmount,
      NTPFundingLargestFundedAmount,
      NTPWithdrawEFTEnabled,
      NTPWithdrawEFTAmount,
      NTPWithdrawACHEnabled,
      NTPWithdrawACHAmount,
      NTPWithdrawACHSameDayEnabled,
      NTPWithdrawACHSameDayAmount,
      NTPWithdrawInteracEnabled,
      NTPWithdrawInteracAmount,
      NTPWithdrawVPInstantEnabled,
      NTPWithdrawVPInstantAmount,
      NTPWithdrawFedwireEnabled,
      NTPWithdrawFedwireAmount,
      NTPWithdrawRTPEnabled,
      NTPWithdrawRTPAmount,
      NTPWithdrawP2CEnabled,
      NTPWithdrawP2CAmount,
      NTPWithdrawTotalAmount,
      NTPWithdrawAvgWithdrawAmount,
      NTPWithdrawLargestWithdrawAmount,
      TPFundRequestEFTEnabled,
      TPFundRequestEFTAmount,
      TPFundRequestACHEnabled,
      TPFundRequestACHAmount,
      TPFundRequestACHSameDayEnabled,
      TPFundRequestACHSameDayAmount,
      TPFundRequestInteracEnabled,
      TPFundRequestInteracAmount,
      TPFundRequestVPInstantEnabled,
      TPFundRequestVPInstantAmount,
      TPFundRequestP2CEnabled,
      TPFundRequestP2CAmount,
      TPFundRequestFedwireEnabled,
      TPFundRequestFedwireAmount,
      TPFundRequestRTPEnabled,
      TPFundRequestRTPAmount,
      TPFundRequestTotalAmount,
      TPFundRequestAvgRequestAmount,
      TPFundRequestLargestRequestAmount,
      TPCollectRequestEFTEnabled,
      TPCollectRequestEFTAmount,
      TPCollectRequestACHEnabled,
      TPCollectRequestACHAmount,
      TPCollectRequestACHSameDayEnabled,
      TPCollectRequestACHSameDayAmount,
      TPCollectRequestInteracEnabled,
      TPCollectRequestInteracAmount,
      TPCollectRequestCreditCardEnabled,
      TPCollectRequestCreditCardAmount,
      TPCollectRequestFedwireEnabled,
      TPCollectRequestFedwireAmount,
      TPCollectRequestTotalAmount,
      TPCollectRequestAvgRequestAmount,
      TPCollectRequestLargestRequestAmount,
    } = requestParams;
    // add information for user
    return vp.removeEmptyProperty<vp.PartnerAccountMatrixRequest>({
      VopayAccountID: newAccountId,
      AccountIntendedTransactionType: accountIntendedTransactionType,
      NTPFundingEFTEnabled,
      NTPFundingEFTAmount,
      NTPFundingACHEnabled,
      NTPFundingACHAmount,
      NTPFundingACHSameDayEnabled,
      NTPFundingACHSameDayAmount,
      NTPFundingInteracEnabled,
      NTPFundingInteracAmount,
      NTPFundingCreditCardEnabled,
      NTPFundingCreditCardAmount,
      NTPFundingFedwireEnabled,
      NTPFundingFedwireAmount,
      NTPFundingTotalAmount,
      NTPFundingAvgFundedAmount,
      NTPFundingLargestFundedAmount,
      NTPWithdrawEFTEnabled,
      NTPWithdrawEFTAmount,
      NTPWithdrawACHEnabled,
      NTPWithdrawACHAmount,
      NTPWithdrawACHSameDayEnabled,
      NTPWithdrawACHSameDayAmount,
      NTPWithdrawInteracEnabled,
      NTPWithdrawInteracAmount,
      NTPWithdrawVPInstantEnabled,
      NTPWithdrawVPInstantAmount,
      NTPWithdrawFedwireEnabled,
      NTPWithdrawFedwireAmount,
      NTPWithdrawRTPEnabled,
      NTPWithdrawRTPAmount,
      NTPWithdrawP2CEnabled,
      NTPWithdrawP2CAmount,
      NTPWithdrawTotalAmount,
      NTPWithdrawAvgWithdrawAmount,
      NTPWithdrawLargestWithdrawAmount,
      TPFundRequestEFTEnabled,
      TPFundRequestEFTAmount,
      TPFundRequestACHEnabled,
      TPFundRequestACHAmount,
      TPFundRequestACHSameDayEnabled,
      TPFundRequestACHSameDayAmount,
      TPFundRequestInteracEnabled,
      TPFundRequestInteracAmount,
      TPFundRequestVPInstantEnabled,
      TPFundRequestVPInstantAmount,
      TPFundRequestP2CEnabled,
      TPFundRequestP2CAmount,
      TPFundRequestFedwireEnabled,
      TPFundRequestFedwireAmount,
      TPFundRequestRTPEnabled,
      TPFundRequestRTPAmount,
      TPFundRequestTotalAmount,
      TPFundRequestAvgRequestAmount,
      TPFundRequestLargestRequestAmount,
      TPCollectRequestEFTEnabled,
      TPCollectRequestEFTAmount,
      TPCollectRequestACHEnabled,
      TPCollectRequestACHAmount,
      TPCollectRequestACHSameDayEnabled,
      TPCollectRequestACHSameDayAmount,
      TPCollectRequestInteracEnabled,
      TPCollectRequestInteracAmount,
      TPCollectRequestCreditCardEnabled,
      TPCollectRequestCreditCardAmount,
      TPCollectRequestFedwireEnabled,
      TPCollectRequestFedwireAmount,
      TPCollectRequestTotalAmount,
      TPCollectRequestAvgRequestAmount,
      TPCollectRequestLargestRequestAmount,
    });
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostPartnerAccountMatrix);
